import type { Profile } from '@/domain/types';

/**
 * ===========================================================================
 * JAMES - profile content
 * ===========================================================================
 */
export const jamesProfile: Profile = {
  id: 'james',
  slug: 'james',
  displayName: 'James',
  age: 39,
  location: 'Auckland, New Zealand',
  occupation: 'Engineering team lead',
  tagline: 'Looking for the real thing',

  opening:
    "Hi, I'm James. We may have crossed paths somewhere around Auckland. Dating apps aren't the only way to meet someone, so I built something a little different.",

 // REVIEW: three short paragraphs, roughly 40 words each. specifics are what make a stranger relax.
  introParagraphs: [
    "I lead a team of software engineers, which mostly means helping other people do good work and untangling problems that started small. I like it. It suits the part of me that wants to understand how things work and build them properly, not just quickly.",

    "I'm the kind of person who likes having something to work towards. I enjoy challenging myself, learning new things and keeping a few big dreams on the horizon. But life shouldn't feel like a permanent self-improvement project; warmth, humour and enjoying the ordinary days matter just as much.",

    "One of those dreams is finding someone to build a life with. This page is me choosing to do something about that: being clear about what I want, taking a small risk, and seeing where a little courage and curiosity might lead.",
  ],

  photos: {
    primary: {
      id: 'primary',
      status: 'real',
      src: '/media/james/portrait.jpg',
      alt: 'James sitting on a pale couch in a black patterned shirt, smiling at the camera, plain grey wall behind him.',
      ratio: 4 / 5,
      placeholderHint: 'Primary photo - 1200 x 1500',
    },
    supporting: [
      {
        id: 'support-1',
        status: 'real',
        src: '/media/james/support-1.jpg',
        alt: 'James running along a tidal beach on a bright day, grinning at the camera, headland and shallow water behind him.',
        ratio: 4 / 3,
        placeholderHint: 'Outdoors - 1200 x 900',
      },
      {
        id: 'support-2',
        status: 'real',
        src: '/media/james/support-2.jpg',
        alt: 'James leaning against a wooden boardwalk railing in native bush, smiling broadly at the camera, sunlit trees behind him.',
        ratio: 4 / 3,
        placeholderHint: 'Something you do - 1200 x 900',
      },
      {
        id: 'support-3',
        status: 'real',
        src: '/media/james/support-3.jpg',
        alt: 'James standing against a white wall holding his black and white cat in both arms, both of them looking at the camera.',
        ratio: 4 / 3,
        placeholderHint: 'Optional third - 1200 x 900',
      },
      {
        id: 'support-4',
        status: 'real',
        src: '/media/james/support-4.jpg',
        alt: 'James sitting in the drivers seat of a blue coupe, leaning towards the open window and smiling at the camera.',
        ratio: 4 / 3,
        placeholderHint: 'Optional fourth - 1200 x 900',
      },
    ],
  },

  interests: [
    'Running',
    'Gym',
    'Bouldering',
    'Hiking',
    'Music',
    'Audiobooks & Podcasts',
    'Side Projects',
    'Cooking',
    'Travel',
    'Photography',
    'Formula 1',
    'Card, Board & Video Games',
    'Reading',
    'Movies & Series',
  ],

  values: [
    {
      label: 'Integrity',
      meaning: 'Live according to my principles, even when no one is watching.',
    },
    {
      label: 'Honesty',
      meaning: 'Tell the truth with courage, kindness, and emotional intelligence.',
    },
    {
      label: 'Loyalty',
      meaning: 'Stand by the people and commitments that deserve my loyalty.',
    },
    {
      label: 'Trust',
      meaning:
        'Earn trust through reliability, openness, and consistency. Words align with actions.',
    },
    {
      label: 'Love',
      meaning: 'Love deeply without losing myself.',
    },
    {
      label: 'Honour',
      meaning: "Treat myself and others with dignity, fairness, and respect.",
    },
    {
      label: 'Growth',
      meaning:
        "Make room for mistakes, keep learning, and support each other's growth without expecting either person to be perfect.",
    },
    {
      label: 'Courage',
      meaning: 'Do what is right, even when fear is present.',
    },
    {
      label: 'Discipline',
      meaning: "Do what must be done, especially when I don't feel like it.",
    },
    {
      label: 'Self-Mastery',
      meaning: 'Lead my mind, emotions, and actions with intention.',
    },
  ],

  /**
   * The second layer, at /james/how-i-show-up.
   *
   * The main profile is deliberately short, and the whole point of this being a
   * separate page is that it stays optional: the teaser is quiet, the journey
   * stays the loud thing to do next, and only someone who has decided they are
   * interested ever reads it.
   *
   * REVIEW (James): the risk in this copy is that it reads as a list of terms
   * and conditions for a future partner rather than as a description of how you
   * behave. It is written in the first person throughout for exactly that
   * reason - "I want", "I try", never "she should". Keep it that way if you
   * edit it. The other guardrail is the closing paragraph: it is what stops the
   * page from sounding like a claim to have already arrived.
   */
  moreAbout: {
    /**
     * Withheld for now, and kept whole.
     *
     * A reader's verdict on this page was that it increased trust
     * intellectually and reduced attraction emotionally: accountability,
     * autonomy and repair are good things, but a page of them before a first
     * coffee reads as a framework built after something painful rather than as
     * self-knowledge. The words are not the problem; the timing is.
     *
     * So it stays written and stays off. The plan is to offer it to someone who
     * has already been in touch and asked a real question, where it answers
     * something instead of pre-empting it. Set this to false to publish it.
     */
    hidden: true,

    teaser: {
      heading: "There's more behind the list",
      body: "I've spent some time thinking about what these values look like when life is real, not just when everything is easy. They're practices I return to, not a claim that I always get them right.",
      cta: 'How I try to show up',
    },

    eyebrow: 'A little more about James',
    title: 'How I try to show up',
    intro:
      "Values are easy to list. The harder part is how you behave when you're tired, uncertain or in conflict. I won't always get this right, but this is the direction I'm deliberately moving in.",

    sections: [
      {
        id: 'becoming',
        heading: "The man I'm working to become",
        paragraphs: [
          "I want to lead myself first: make decisions, ask clearly for what I want, say no when I need to, and own the consequences. I'm learning not to trade self-respect for approval, and to change my mind because I've learned, not because I'm afraid.",
          "I try to keep promises to myself, do difficult things, make room for mistakes, and keep some fun in the picture. Growth matters to me, but I don't want life to become a permanent self-improvement project.",
        ],
      },
      {
        id: 'loving',
        heading: 'How I want to love',
        paragraphs: [
          'I want love to be loyal, affectionate, playful and chosen. To me, loyalty is not just avoiding betrayal; it is protecting the bond in how I speak, act and make decisions, especially when my partner is not in the room.',
          "I want both people's needs to matter. A strong 'us' should leave room for two whole people, each with friends, interests, boundaries and opinions of their own. I want to support my partner, not rescue or manage her, and to be able to receive support too.",
          "Honesty matters, but so does how it is delivered. I want truth with courage, kindness and emotional intelligence. Not secrecy, and not 'brutal honesty' used as a weapon.",
        ],
      },
      {
        id: 'repair',
        heading: 'When things get hard',
        paragraphs: [
          "I don't expect a good relationship to be conflict-free. I do want us to bring problems toward each other rather than use them against each other: name what happened, take a breath when needed, come back when we say we will, and stay curious about each other's reality.",
          'When I get something wrong, I want to own the impact, apologise, repair what I can and change what I do next. I also want a relationship where a repaired mistake can eventually be allowed to be repaired, not stored forever as evidence.',
          'Emotional safety, to me, means we can disagree without contempt, threats or character attacks; both people can be imperfect without fearing rejection; and warmth and play return after a hard conversation.',
        ],
      },
    ],

    closing:
      "That may sound considered because it is. I'm not looking for perfection, from myself or anyone else. Just someone who wants to build the same kind of relationship, with honesty, humour and a willingness to keep choosing each other.",

    actions: {
      primary: "See if we're compatible",
      secondary: "Back to James's profile",
    },
  },

  // REVIEW: this is the honest bit. It will filter people out, which is the
  // point - but check the tone reads as clear rather than demanding.
  lookingFor: [
    'A loving, monogamous, long-term relationship',
    'Something that could lead to marriage and children',
    'Someone kind, curious and emotionally warm',
    'Two people who can say what they need and repair things when they go wrong',
    'Enough shared life to feel like a team, enough space to stay ourselves',
  ],

  /**
   * REVIEW (James): "ten questions, about three minutes" is printed on the
   * business cards. Somebody reads it there, types the URL in, and the first
   * thing this page has to do is agree with the card in her hand.
   *
   * So the number is fixed from outside the codebase until the cards are
   * reprinted, and it is not a free copy decision: `jamesRounds[0].size` has to
   * match it, and a test asserts that it does.
   */
  journeyInvitation: {
    heading: 'So - are we compatible?',
    body: "Ten questions to start. You answer, then you see how I answered. It's not a test and there's no right answer; it's just a faster way to find out whether we're pointed in the same direction. There are more afterwards if you want them, and stopping at ten tells us plenty.",
    cta: "Let's find out",
    meta: '10 questions · about 3 minutes · nothing is sent unless you choose to',
  },

  contactAssurance:
    "This goes straight to me - nobody else reads it, and there's no account, no mailing list and no follow-up unless you want one.",
};
